import {useState} from 'react';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import {Box} from '@material-ui/core';
import AliceCarousel, {EventObject} from 'react-alice-carousel';

import {
  xiaomiImages,
  samsungImages,
  samsungStepDescription,
  xiaomiStepDescription,
  iosStepDescriptionMethod1,
  iosStepDescriptionMethod2,
  iosStepDescriptionMethod3,
  iosImagesMethod1,
  iosImagesMethod2,
  iosImagesMethod3,
  androidStepDescription,
  androidImages,
} from './Guides';
import ModalShowGuideHeader from './ModalShowGuideHeader';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  stepTitle: {
    fontSize: 18,
    lineHeight: 1.22,
    marginBottom: 13,
  },
  card: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    padding: theme.spacing(1.25, 2),
    overflowY: 'auto',
  },
  slider: {
    color: theme.palette.common.black,
    textAlign: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  slideImg: {
    maxHeight: '55vh',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
  },
  dot: {
    height: 6,
    width: 6,
    backgroundColor: '#d8d8d8',
    borderRadius: '50%',
    transition: 'all 0.2s',
    '& + $dot': {
      marginLeft: 6,
    },
    '&.active': {
      backgroundColor: theme.palette.common.black,
    },
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  isDeviceIos: boolean;
  isSamsungBrowser: boolean;
  isXiaomiBrowser: boolean;
  isAndroidBrowser: boolean;
  method?: number;
}

const ModalShowGuide: React.FC<Props> = ({
  open,
  onClose,
  isDeviceIos,
  isSamsungBrowser,
  isXiaomiBrowser,
  isAndroidBrowser,
  method = null,
}) => {
  const s = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);

  let slides: string[] = [];
  let stepDescription: React.ReactNode[] = [];

  if (isDeviceIos && method === 1) {
    slides = iosImagesMethod1;
    stepDescription = iosStepDescriptionMethod1(s.stepTitle);
  }

  if (isDeviceIos && method === 2) {
    slides = iosImagesMethod2;
    stepDescription = iosStepDescriptionMethod2(s.stepTitle);
  }

  if (isDeviceIos && method === 3) {
    slides = iosImagesMethod3;
    stepDescription = iosStepDescriptionMethod3(s.stepTitle);
  }

  if (isSamsungBrowser) {
    slides = samsungImages;
    stepDescription = samsungStepDescription(s.stepTitle);
  }

  if (isXiaomiBrowser) {
    slides = xiaomiImages;
    stepDescription = xiaomiStepDescription(s.stepTitle);
  }

  if (isAndroidBrowser && !isSamsungBrowser) {
    slides = androidImages;
    stepDescription = androidStepDescription(s.stepTitle);
  }

  const handleSlideChange = (e: EventObject) => {
    setActiveIndex(e.item);
  };

  const handleClose = () => {
    onClose();
    setActiveIndex(0);
  };

  const items = slides.map((src, index) => (
    <div key={src}>
      <div className="h2 semibold">Step {index + 1}</div>
      {stepDescription[index]}
      <img className={s.slideImg} src={src} alt={`slide-${index}`} />
    </div>
  ));

  return (
    <Modal open={open} onClose={onClose}>
      <div className={s.wrapper}>
        <ModalShowGuideHeader onClose={handleClose} />
        <div className={s.card}>
          <div className={s.slider}>
            <AliceCarousel
              autoWidth
              items={items}
              onSlideChanged={handleSlideChange}
              disableButtonsControls
              disableDotsControls
              activeIndex={activeIndex}
              renderKey={items.length}
            />
          </div>
          <div className={s.dots}>
            {slides.map((src, index) => (
              <div className={clsx(s.dot, {active: index === activeIndex})} key={`dot-${src}`} />
            ))}
          </div>
        </div>
        {activeIndex === slides.length - 1 && (
          <Box mx={1} mb={1}>
            <Button fullWidth onClick={handleClose}>
              OK
            </Button>
          </Box>
        )}
      </div>
    </Modal>
  );
};

export default ModalShowGuide;
